import { Box, Typography, IconButton, Paper, Button, TextField, InputBase } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useRef, useState } from "react";
import { ModelHandler } from "../../interface/model-handler";
import Toast from "../../utils/Toast";
import { DemoItem } from "@mui/x-date-pickers/internals/demo";
import { GridArrowUpwardIcon } from "@mui/x-data-grid";

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckIcon from '@mui/icons-material/Check';
import UndoIcon from '@mui/icons-material/Undo';
import EditIcon from '@mui/icons-material/Edit';
import LoadingCircle from "../../utils/LoadingCircle";

type Props = {
  open: boolean;
  onClose: () => void;
  draft: string;
  pressList: string[];
  onUpdatePress: (index: number, newContent: string) => void;
};

const tabs = ["전체 수정", "문단 별 수정", "문장 별 수정"] as const;
type TabType = typeof tabs[number];

const tabToIndex = {
  '문장 별 수정': 0,
  '문단 별 수정': 1,
  '전체 수정': 2,
};

export default function SuggestionDrawer2({ open, onClose, draft, pressList, onUpdatePress }: Props) {
  // const [selectedReplacement, setSelectedReplacement] = useState<string | null>(null);
  const toastRef: any = useRef();
  const drawerRef = useRef<HTMLDivElement>(null);
  const scrollRef: any = useRef();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [selectedTab, setSelectedTab] = useState<TabType>("전체 수정");
  const currentIndex = tabToIndex[selectedTab];
  const currentContent = pressList[currentIndex] ?? '';

  const [command, setCommand] = useState("");
  const [result, setResult] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [atBottom, setAtBottom] = useState(false);
  // const [editedText, setEditedText] = useState("");
  // const [editingIndex, setEditingIndex] = useState<number | null>(null);
  // const [appliedMap, setAppliedMap] = useState<{ [index: number]: boolean }>({});

  const [model, setModel] = useState("");
  const modelHandler = new ModelHandler(model);
  const [aiApi, handleStreamResponse] = modelHandler.getModelElements();
  const [streamReader, setStreamReader] = useState<ReadableStreamDefaultReader | null>(null);
  
  useEffect(() => {
    modelHandler
      .getUsingModel()
      .then((usingModel) => {
        setModel(usingModel);
      })
      .catch((errorMsg: any) => {
        toastRef.current?.toast("" + errorMsg, "error", 4000, {
          vertical: "top",
          horizontal: "center",
        });
      });
      // get_llm_model();
    return () => {
      setIsLoading(false);
    };
  }, []);

  // useEffect(() => {
  //   setEditedText("");
  //   setEditingIndex(null);
  //   setAppliedMap({});
  // }, [selected]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // 예외 처리: highlight 또는 highlight-selected 클래스 클릭 시 무시
      if (
        target.closest(".highlight") || // 클릭한 요소가 highlight 내부에 있을 경우
        (drawerRef.current && drawerRef.current.contains(target))
      ) {
        return; // 닫지 않음
      }

      // highlight-selected 클래스 전부 제거
      const highlighted = document.querySelectorAll(".highlight-selected");
      highlighted.forEach((el) => el.classList.remove("highlight-selected"));

      // drawer 닫기
      // onClose();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  if (!open) return null; // 닫혔을 땐 렌더링하지 않음

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;

    const scrollTop = el.scrollTop;
    const scrollHeight = el.scrollHeight;
    const clientHeight = el.clientHeight;

    setAtBottom(scrollTop + clientHeight >= scrollHeight - 5);
  };

  const splitTextByLength = (text: string, maxLength: number) => {
    const sentences = text.match(/[^.!?]+[.!?]+[\])'"`’”]*|\s*$/g) || [];
    const parts: string[] = [];
    let buffer = "";

    for (const sentence of sentences) {
      if ((buffer + sentence).length <= maxLength) {
        buffer += sentence;
      } else {
        parts.push(buffer.trim());
        buffer = sentence;
      }
    } 
    if (buffer.trim()) parts.push(buffer.trim());
    return parts;
  };

  const splitTextByParagraph = (text: string, maxLength: number) => {
    // 문단 기준으로 먼저 나눔 (두 줄 이상 띄운 경우를 문단으로 간주)
    const paragraphs = text.split(/\n\s*\n/);
    const parts: string[] = [];
    let buffer = "";

    for (const para of paragraphs) {
      const trimmed = para.trim();
      if (!trimmed) continue;

      // 현재 buffer에 추가해도 maxLength를 넘지 않으면 이어붙임
      if ((buffer + "\n\n" + trimmed).trim().length <= maxLength) {
        buffer += (buffer ? "\n\n" : "") + trimmed;
      } else {
        if (buffer) parts.push(buffer.trim());
        buffer = trimmed;
      }
    }

    if (buffer.trim()) parts.push(buffer.trim());
    return parts;
  };

  const generatePrompt = (cmd: string, draft: string) => {
    return {
      command: cmd,
      userCommand: command,
      draft: draft,
    };
  }

  const processAllPress = async (cmd: string, press: string, maxLength = 1000): Promise<string> => {
    const parts = splitTextByParagraph(press, maxLength);
    const prompts = parts.map((part) => generatePrompt(cmd, part));
    console.log("parts: ", parts);

    const responses = await Promise.all(prompts.map(async (prompt) => {
      const streamResponse = await aiApi.postStream(prompt);
      const result = await genStreamResponse(streamResponse);
      return result;
    }));
    console.log("responses: ", responses);

    // const mergePrompt = {
    //   command: "desk_press_release_merge",
    //   draft: responses.join("\n\n")
    // };

    // const streamResponse = await aiApi.postStream(mergePrompt);
    // const finalArticle = await genStreamResponse(streamResponse);
    // console.log("finalArticle: ", finalArticle);
    
    return responses.join("\n\n");
  }
  
  const generateSuggestion = async () => {
    setIsLoading(true);

    try {
      // const [aiApi, handleStreamResponse] = modelHandler.getModelElements();
    
      const headlineParam = {
        command: "desk_press_headline_set_v2",
        userCommand: command,
        draft: draft,
      };

      const headlineStream = await aiApi.postStream(headlineParam);
      const headline = await genStreamResponse(headlineStream);

      const leadlineParam = {
        command: "desk_press_leadline",
        userCommand: command,
        draft: draft,
      };

      const leadlineStream = await aiApi.postStream(leadlineParam);
      const leadline = await genStreamResponse(leadlineStream);

      const body = await processAllPress("desk_press_release_v" + (currentIndex + 1) + "_test", draft, 900)

      // const param = {
      //   command: "desk_press_release_v" + (currentIndex + 1) + "_test",
      //   userCommand: command,
      //   draft: draft,
      // };

      // // const [
      // //   headlineStream, 
      // //   bodyStream
      // // ] = await Promise.all([
      // //   aiApi.postStream(headlineParam),
      // //   aiApi.postStream(param),
      // // ]);
      
      // // const [
      // //   headline,
      // //   body
      // // ] = await Promise.all([
      // //   genStreamResponse(headlineStream),
      // //   genStreamResponse(bodyStream)
      // // ])

      const press = headline + '\n\n' + leadline + '\n\n' + body;

      onUpdatePress(currentIndex, press);
      setIsLoading(false);

      // handleStreamResponse(
      //   headlineStream, 
      //   (result: string) => {
      //     if (result?.trim()) {
      //       onUpdatePress(currentIndex, result);
      //     }
      //   },
      //   setStreamReader,
      //   (finalText: string) => {
      //     onUpdatePress(currentIndex, finalText);
      //   }
      // );

      // handleStreamResponse(
      //   bodyStream, 
      //   (result: string) => {
      //     if (result?.trim()) {
      //       onUpdatePress(currentIndex, result);
      //     }
      //   },
      //   setStreamReader,
      //   (finalText: string) => {
      //     onUpdatePress(currentIndex, finalText);
      //   }
      // );
    } catch (error) {
      console.error("Error generating suggestion data: ", error);
    } finally {
      setIsLoading(false);
      setCommand("");
    }
  };

  const genStreamResponse = async (streamRes: ReadableStream): Promise<string> => {
    const reader = streamRes?.getReader();
    const decoder = new TextDecoder();

    let done = false;
    let content = "";

    while (!done) {
      const { value, done: doneReading } = await reader.read();
      done = doneReading; // 스트림 완료시 true
      const chunkValue = decoder.decode(value);
      content = content + chunkValue;
    }
    try {
      content = content
        .replace(/^```json\s*/, '')  // 앞부분 제거
        .replace(/```$/, '')         // 뒷부분 제거
        .trim();

      return content;
    } catch (error) {
      // console.log("content: ", content);
      // console.log("content Type: ", typeof content);
      return JSON.stringify({ code: "500", response: { error_msg: "json parse error" } });
    }
  };
  
  return (
    <>
    <Box
      ref={drawerRef}
      sx={{
        height: 'calc(100vh - 108px)',
        backgroundColor: "#FFFFFF",
        // transform: open ? "translateX(0)" : `translateX(${drawerWidth}px)`,
        transition: "transform 0.3s ease-in-out",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: "0px 8px 20px 0px rgba(0, 0, 0, 0.1)",
        border: '1px solid #2E3341',
        borderRadius: "24px", // 선택 사항: 더 둥글게 하고 싶으면
        boxSizing: "border-box",
        p: "26px 32px",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "center", mb: '20px' }}>
        <img src="/images/icon-ai-recommend.png" style={{ width: '18px', height: '18px'}} />
        <Typography sx={{ fontWeight: 700, fontSize: "24px" }}>AI 데스킹</Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "center", mb: 2 }}>
        {tabs.map((tab) => (
          <button 
            onClick={() => {
              setSelectedTab(tab);
              // scrollRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); // 왜 안됨.
            }}
            style={{
              width: "94px",
              height: "32px",
              // padding: '11px 13px',
              borderRadius: '8px',
              border: '1px solid #2E3341',
              backgroundColor: selectedTab === tab ? "#2E3341" : "#fff",
              color: selectedTab === tab ? "#fff" : "#2E3341",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: 500,
            }}
          >
            {tab}
          </button>
        ))}
      </Box>

      <Box
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          flex: '1 1 auto', 
          overflow: 'hidden', 
          position: 'relative', 
          marginBottom: '32px' 
        }}
      >
        <Box 
          ref={scrollRef}
          onScroll={handleScroll}
          sx={{ 
            flex: 1, 
            overflowY: "auto",
            '&::-webkit-scrollbar': { display: 'none' },
            '-ms-overflow-style': 'none',
            'scrollbar-width': 'none',
            // mb: 4,
            // // 스크롤바 투명 처리
            // // FireFox 
            // scrollbarWidth: 'thin',
            // scrollbarColor: '#ECECEC transparent',

            // // WebKit (Chrome, Safari)
            // '&::-webkit-scrollbar': {
            //   width: '6px',
            // },
            // '&::-webkit-scrollbar-track': {
            //   background: 'transparent', // 트랙 배경 투명
            // },
            // '&::-webkit-scrollbar-thumb': {
            //   backgroundColor: '#ECECEC', // 필요 시 스크롤바 컬러 조절
            //   borderRadius: '3px',
            // },
          }}
        >
          <Typography sx={{ whiteSpace: 'pre-line' }}>
            {currentContent}
          </Typography>
        </Box>

        {/* 하단 고정 Gradient (스크롤 시 사라짐) */}
        {!atBottom && scrollRef.current?.scrollHeight > scrollRef.current?.clientHeight && (
          <Box
            sx={{
              position: 'absolute',
              width: "100%",
              bottom: 0,
              left: 0,
              right: 0,
              height: '100px',
              background: 'linear-gradient(to top, white 0%, rgba(255,255,255,0) 100%)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
        )}
      </Box>

      {/* 프롬프트 입력 영역 */}
      <Paper
        component="form"
        elevation={3}
        onSubmit={(e) => {
          e.preventDefault();
          generateSuggestion();
          // deskPress();
        }}
        sx={{
          // width: '100%',
          // maxWidth: '280px',
          // m: 4,
          // p: '6px 8px 8px 16px',
          // px: 2,
          // py: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: '16px',
          backgroundColor: '#F5F5F5',
          boxShadow: '0 0px 0px rgba(0, 0, 0, 0.1)',
        }}
      >
        <InputBase
          multiline
          maxRows={9}
          sx={{
            // flex: 1,
            width: '100%',
            height: '100%',
            overflowY: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
            '-ms-overflow-style': 'none',
            'scrollbar-width': 'none',
            color: '#333',
            '&::placeholder': { color: '#a8a8b3' },
            p: '14px 20px',
            '& textarea': {
              backgroundColor: 'transparent',
              lineHeight: 1.5,

              // 스크롤바 투명 처리
              // FireFox 
              scrollbarWidth: 'thin',
              scrollbarColor: '#999 transparent',

              // WebKit (Chrome, Safari)
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent', // 트랙 배경 투명
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0, 0, 0, 0.2)', // 필요 시 스크롤바 컬러 조절
                borderRadius: '3px',
              },
            },
          }}
          placeholder="추가 요청사항을 입력하거나, 바로 전송해 보도자료를 받아보세요"
          value={command}
          onChange={(e) => setCommand(e.target.value)}
          disabled={isLoading}
        />
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <IconButton
            type="submit"
            sx={{
              backgroundColor: '#2E3341',
              color: '#fff',
              width: 32,
              height: 32,
              mr: 2,
              mb: 2,
              borderRadius: '50%',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            <ArrowUpwardIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Paper>
    </Box>
    {/* 로딩바 */}
    <LoadingCircle loading={isLoading} />
    <Toast ref={toastRef} />
    </>
  );
}
